import Add from '@mui/icons-material/Add';
import Box, { BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FormStateChangeProps } from '~formsShared/types';
import useAccountsAdmin from '~hooks/admin/useAccountsAdmin/useAccountsAdmin';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { Pagination, PaginationLink } from '~services/pagination';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getPageNumber } from '~utils/pagination';

import {
  AddConnectionForm,
  AddConnectionFormHandler,
  AddConnectionFormValues,
} from './AddConnectionForm';

const SelectorConnectionCTAWrapper = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  width: '540px',
  marginBottom: '24px',
}));

interface AddConnectionCTADialogProps {
  selectedCompany: Nullable<CompanyBasic>;
  onSuccessCallback?: (selectedCompanyId: string) => void;
}

const AddConnectionCTADialog = ({
  selectedCompany,
  onSuccessCallback,
}: AddConnectionCTADialogProps) => {
  const { createConnectedAccountByTreadId } = useAccountsAdmin();
  const addConnectionFormRef = useRef<AddConnectionFormHandler | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const onValidateCompanyForm = ({ isDirty, isValid = false }: FormStateChangeProps) => {
    setIsDirty(isDirty);
    setIsValid(isValid);
  };

  const onSuccess = () => {
    setIsDialogOpen(false);

    if (selectedCompany) {
      onSuccessCallback?.(selectedCompany.id);
    }
  };

  const onSubmitAction: SubmitHandler<AddConnectionFormValues> = async ({
    senderCompanyId,
    receiverTreadId,
    accountTypes,
  }) => {
    if (!receiverTreadId) return;

    setIsUpdating(true);

    try {
      await createConnectedAccountByTreadId({
        senderCompanyId,
        receiverTreadId,
        accountTypes,
      });
      onSuccess();
    } catch {
      // Error is already handled in the hook
    } finally {
      setIsUpdating(false);
    }
  };

  const onSubmitCompanyForm = () => {
    addConnectionFormRef.current?.submit(onSubmitAction);
  };

  return (
    <>
      <Button
        color="primary"
        data-test-id="create-company-btn"
        disabled={!selectedCompany?.id}
        onClick={() => setIsDialogOpen(true)}
        startIcon={<Add />}
      >
        {t('company.add_connected_account')}
      </Button>

      {selectedCompany && (
        <Dialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          preventCloseOnBackdropClick
        >
          <DialogHeader onClose={() => setIsDialogOpen(false)}>
            {`${t('account.add_connection_heading')} ${selectedCompany.legalName}`}
          </DialogHeader>

          <DialogContent>
            <AddConnectionForm
              ref={addConnectionFormRef}
              senderCompanyId={selectedCompany.id}
              onFormStateChange={onValidateCompanyForm}
            />
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={isUpdating}
              onClick={() => setIsDialogOpen(false)}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              data-test-id="connect-companies-btn"
              disabled={!isDirty || !isValid || isUpdating}
              loading={isUpdating}
              onClick={onSubmitCompanyForm}
            >
              {t('actions.connect')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const getConnectedLabel = (account: Account) =>
  account.connectedCompany ? t('actions.yes') : t('actions.no');

const ACCOUNT_COLUMNS: Array<DataTableColumn<Account>> = [
  {
    field: 'name',
    headerName: t('form_fields.account_name'),
    valueGetter: (account) => account.name,
  },
  {
    field: 'company',
    headerName: t('form_fields.company'),
    valueGetter: (account) => account.company?.legalName,
  },
  {
    field: 'company.connected',
    headerName: t('form_fields.connected'),
    valueGetter: getConnectedLabel,
    renderCell: (account) => (
      <Tooltip title={getConnectedLabel(account)}>
        <Chip
          color={account.connectedCompany ? 'success' : 'info'}
          label={getConnectedLabel(account)}
          variant="outlined"
        />
      </Tooltip>
    ),
  },
  {
    field: 'accountTypes',
    headerName: t('form_fields.type'),
    valueGetter: (account) => account.accountTypes.join(', '),
    renderCell: (account) => (
      <>
        {[...account.accountTypes].sort().map((type, index) => (
          <Tooltip title={type} key={type}>
            <Chip
              color={type === AccountType.VENDOR ? 'info' : 'success'}
              label={type}
              sx={{ ml: index !== 0 ? 1 : 0 }}
              variant="outlined"
            />
          </Tooltip>
        ))}
      </>
    ),
  },
  {
    field: 'address.fullAddress',
    headerName: t('form_fields.address'),
    valueGetter: ({ address }) => {
      if (!address) return '';
      const { streetAddress, premise, country, administrativeArea } = address;
      const street = [streetAddress, premise].filter(Boolean).join(' ');

      return [street, administrativeArea, country?.name].filter(Boolean).join(', ');
    },
  },
  {
    field: 'createdAt',
    headerName: t('common.created_on'),
    valueGetter: (account) => dateFormat(account.createdAt),
    renderCell: (account) => (
      <Tooltip title={`${t('common.last_update')}: ${dateFormat(account.updatedAt)}`}>
        <Typography component="span">{dateFormat(account.createdAt)}</Typography>
      </Tooltip>
    ),
  },
];

export default function AdminConnectionsDataGrid() {
  const {
    control,
    formState: { errors },
    watch,
  } = useForm();
  const { getAccountsByCompanyId } = useAccount();
  const selectedCompany: Nullable<CompanyBasic> = watch('connections-companies-selector');
  const previousSelectedCompany = usePrevious<Nullable<CompanyBasic>>(selectedCompany);
  const [accountsByCompanyId, setAccountsByCompanyId] = useState<Account[]>([]);
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>();
  const issueAccountsRequestSignal = useLatestRequestSignal();
  const isLoading = isAccountLoading;
  const { searchValue, setSearchQueryValue } = useDataGridSearch();

  const fetchAccountsByCompanyId = async (
    companyId: string,
    link?: PaginationLink,
    cursor?: string,
  ) => {
    if (!companyId) return;

    const signal = issueAccountsRequestSignal();
    setIsAccountLoading(true);
    try {
      const result = await getAccountsByCompanyId({
        companyId,
        cursor,
        link,
        searchQuery: searchValue,
        signal,
      });
      if (signal.aborted) return;

      setAccountsByCompanyId(result.data);
      setPagination((previousPagination) => ({
        ...result.pagination,
        page: getPageNumber(previousPagination?.page ?? 1, link),
      }));
    } catch {
      // Error toast is handled in the hook.
    } finally {
      if (!signal.aborted) {
        setIsAccountLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedCompany && previousSelectedCompany?.id !== selectedCompany?.id) {
      fetchAccountsByCompanyId(selectedCompany.id);
    } else if (!selectedCompany) {
      // aborts the previous company's request, which would otherwise refill the grid
      issueAccountsRequestSignal();
      setIsAccountLoading(false);
      setAccountsByCompanyId([]);
      setPagination(undefined);
    }
  }, [previousSelectedCompany?.id, selectedCompany?.id]);

  const previousSearchValue = usePrevious(searchValue);
  const searchValueDidChange = previousSearchValue !== searchValue;

  useEffect(() => {
    if (searchValueDidChange && selectedCompany?.id) {
      fetchAccountsByCompanyId(selectedCompany?.id);
    }
  }, [searchValueDidChange, selectedCompany?.id]);

  return (
    <Box>
      <SelectorConnectionCTAWrapper>
        <CompanySelectorFormField
          clearable
          control={control}
          errors={errors}
          hideLabel
          inputProps={{ placeholder: t('form_fields.company_selector_hint') }}
          name="connections-companies-selector"
          sx={{ flexGrow: 1, flexShrink: 0, maxWidth: '300px' }}
        />

        <AddConnectionCTADialog
          selectedCompany={selectedCompany}
          onSuccessCallback={fetchAccountsByCompanyId}
        />
      </SelectorConnectionCTAWrapper>

      <DataTable
        id="admin-accounts-grid"
        columns={ACCOUNT_COLUMNS}
        loading={isLoading}
        onSearchChange={setSearchQueryValue}
        rows={accountsByCompanyId}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isLoading}
            onNavigate={(link) => {
              if (selectedCompany?.id) {
                void fetchAccountsByCompanyId(
                  selectedCompany.id,
                  link,
                  link ? pagination?.[link] : undefined,
                );
              }
            }}
            pagination={pagination}
          />
        }
      />
    </Box>
  );
}
